import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, User, Phone, EnvelopeSimple, Warning, CheckCircle, Clock, Trash } from "@phosphor-icons/react";

function dotColorByStatus(expired, expiring) {
  if (expired > 0) return "#FF3B30";
  if (expiring > 0) return "#FFD600";
  return "#22c55e";
}

function statusBadge(status) {
  if (status === "expired") return { cls: "bg-[#FF3B30] text-white", label: "SCADUTO", Icon: Warning };
  if (status === "expiring") return { cls: "bg-[#FFD600] text-[#0A0A0A]", label: "IN SCADENZA", Icon: Clock };
  return { cls: "bg-[#22c55e] text-white", label: "OK", Icon: CheckCircle };
}

export default function EmployeeDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/employees/${id}`);
      setData(data);
    } catch {
      toast.error("Dipendente non trovato");
      nav("/personale");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!data) return <Layout><div className="text-[#A3A3A3]">Caricamento…</div></Layout>;
  const { employee, certificates, summary } = data;

  const remove = async () => {
    if (!confirm("Eliminare il dipendente? Gli attestati associati resteranno in archivio.")) return;
    await api.delete(`/employees/${id}`);
    toast.success("Eliminato"); nav("/personale");
  };

  const dotColor = dotColorByStatus(summary.expired, summary.expiring);

  return (
    <Layout>
      <button onClick={()=>nav("/personale")} className="text-[#A3A3A3] flex items-center gap-1 mb-4" data-testid="back-btn">
        <ArrowLeft size={18}/>Personale
      </button>

      <div className="bg-[#1A1A1A] border border-[#333] rounded-md p-4 mb-4">
        <div className="flex items-center gap-4">
          {employee.photo ? (
            <img src={employee.photo} alt="" className="w-20 h-20 rounded-full object-cover border-2 border-[#FFD600]" data-testid="emp-photo-display"/>
          ) : (
            <div className="w-20 h-20 rounded-full bg-[#0A0A0A] border-2 border-[#333] flex items-center justify-center">
              <User size={40} weight="bold" className="text-[#A3A3A3]"/>
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="font-heading text-3xl font-black uppercase tracking-tighter leading-none truncate">{employee.name} {employee.surname || ""}</div>
            <div className="text-sm text-[#FFD600] font-bold uppercase tracking-wider mt-1">{employee.role || "Operaio"}</div>
            <div className="flex items-center gap-1 mt-2">
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: dotColor }}></span>
              <span className="text-xs text-[#A3A3A3] font-mono">{summary.total} attestati</span>
            </div>
          </div>
        </div>
        {(employee.phone || employee.email) && (
          <div className="mt-3 pt-3 border-t border-[#333] space-y-1 text-sm text-[#A3A3A3]">
            {employee.phone && <div className="flex items-center gap-2"><Phone size={14}/>{employee.phone}</div>}
            {employee.email && <div className="flex items-center gap-2"><EnvelopeSimple size={14}/>{employee.email}</div>}
          </div>
        )}
      </div>

      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="bg-[#22c55e]/10 border border-[#22c55e]/30 rounded-md p-3 text-center">
          <div className="font-heading text-3xl font-black text-[#22c55e]">{summary.ok}</div>
          <div className="text-[10px] uppercase tracking-wider text-[#A3A3A3] font-bold">Validi</div>
        </div>
        <div className="bg-[#FFD600]/10 border border-[#FFD600]/30 rounded-md p-3 text-center">
          <div className="font-heading text-3xl font-black text-[#FFD600]">{summary.expiring}</div>
          <div className="text-[10px] uppercase tracking-wider text-[#A3A3A3] font-bold">In scad.</div>
        </div>
        <div className="bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded-md p-3 text-center">
          <div className="font-heading text-3xl font-black text-[#FF3B30]">{summary.expired}</div>
          <div className="text-[10px] uppercase tracking-wider text-[#A3A3A3] font-bold">Scaduti</div>
        </div>
      </div>

      <div className="text-xs uppercase tracking-wider text-[#A3A3A3] font-bold mb-2">Attestati</div>
      <div className="space-y-2 mb-4">
        {certificates.length === 0 ? (
          <div className="bg-[#1A1A1A] border border-[#333] rounded-md p-6 text-center text-[#A3A3A3] text-sm">
            Nessun attestato collegato. Vai su <Link to="/personale" className="text-[#FFD600] font-bold">Personale → Attestati</Link> per aggiungerne uno.
          </div>
        ) : certificates.map((c) => {
          const b = statusBadge(c.status);
          return (
            <div key={c.id} className="bg-[#1A1A1A] border border-[#333] rounded-md p-3 flex items-center gap-3" data-testid={`cert-row-${c.id}`}>
              <span className={`px-2 py-1 rounded text-[10px] font-black uppercase tracking-wider ${b.cls} flex items-center gap-1`}>
                <b.Icon size={12} weight="fill"/> {b.label}
              </span>
              <div className="flex-1 min-w-0">
                <div className="font-bold truncate">{c.title}</div>
                <div className="text-xs text-[#A3A3A3]">
                  Scade: {new Date(c.expiry_date).toLocaleDateString("it-IT")}
                  {c.days_to_expiry !== null && <span className="ml-2 font-mono">({c.days_to_expiry < 0 ? `${-c.days_to_expiry}gg fa` : `tra ${c.days_to_expiry}gg`})</span>}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <button data-testid="delete-emp-btn" onClick={remove}
        className="w-full bg-[#1A1A1A] border-2 border-[#FF3B30]/40 text-[#FF3B30] h-12 rounded-md font-black uppercase flex items-center justify-center gap-2">
        <Trash size={18} weight="bold"/> Elimina dipendente
      </button>
    </Layout>
  );
}
